// src/screens/academique/LabelingTest.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  Stack,
  IconButton,
  Alert,
  Paper,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Send as SendIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as EmojiEventsIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

const LabelingTest = ({ open, test, session, student, onClose, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  
  // Questions mockées pour le test de labellisation
  useEffect(() => {
    if (open && test) {
      const mockQuestions = [
        {
          id: 101,
          question: 'Selon la norme ISO 21500, quel est le rôle principal du chef de projet ?',
          type: 'unique',
          points: 10,
          timer: 45,
          matiere: 'Gestion de projet',
          options: [
            'Gérer les ressources financières uniquement',
            'Planifier, organiser et contrôler le projet pour atteindre ses objectifs',
            'Rédiger la documentation technique',
            'Superviser les fournisseurs'
          ],
          correct: [1]
        },
        {
          id: 102,
          question: 'Parmi ces éléments, lesquels sont des facteurs clés de succès d\'un projet ?',
          type: 'multiple',
          points: 15,
          timer: 60,
          matiere: 'Gestion de projet',
          options: [
            'Une communication efficace',
            'Des objectifs clairs et mesurables',
            'Un budget illimité',
            'L\'engagement des parties prenantes',
            'Une équipe compétente'
          ],
          correct: [0, 1, 3, 4]
        },
        {
          id: 103,
          question: 'Qu\'est-ce que la méthode Agile ?',
          type: 'unique',
          points: 10,
          timer: 45,
          matiere: 'Méthodologies',
          options: [
            'Une approche prédictive avec planification détaillée',
            'Une approche itérative et incrémentale favorisant l\'adaptation au changement',
            'Une méthode de gestion des risques',
            'Un outil de reporting financier'
          ],
          correct: [1]
        },
        {
          id: 104,
          question: 'Expliquez la différence entre un risque et un problème dans la gestion de projet.',
          type: 'open',
          points: 20,
          timer: 90,
          matiere: 'Gestion des risques',
          options: []
        },
        {
          id: 105,
          question: 'Quels sont les 5 processus de base selon le PMBOK ?',
          type: 'multiple',
          points: 15,
          timer: 60,
          matiere: 'Gestion de projet',
          options: [
            'Déclenchement',
            'Planification',
            'Exécution',
            'Surveillance et contrôle',
            'Clôture',
            'Audit'
          ],
          correct: [0, 1, 2, 3, 4]
        }
      ];
      setQuestions(mockQuestions);
      setAnswers({});
      setCurrentIndex(0);
      setTabSwitchCount(0);
      setStartTime(Date.now());
      setTimeRemaining(mockQuestions[0]?.timer || 45);
    }
  }, [open, test]);
  
  // Détection de changement d'onglet (anti-triche)
  useEffect(() => {
    if (!open) return;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabSwitchCount(prev => {
          const newCount = prev + 1;
          setShowWarning(true);
          setTimeout(() => setShowWarning(false), 3000);
          return newCount;
        });
      }
    };
    
    const handleBlur = () => {
      setTabSwitchCount(prev => {
        const newCount = prev + 1;
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 3000);
        return newCount;
      });
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [open]);
  
  // Timer pour chaque question
  useEffect(() => {
    if (!open || timeRemaining === null || submitting) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Passer automatiquement à la question suivante
          if (currentIndex < questions.length - 1) {
            setCurrentIndex(prevIndex => prevIndex + 1);
            return questions[currentIndex + 1]?.timer || 45;
          } else if (currentIndex === questions.length - 1) {
            // Dernière question, soumettre automatiquement
            handleSubmit();
            return 0;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [open, timeRemaining, currentIndex, questions, submitting]);
  
  // Mettre à jour le timer quand la question change
  useEffect(() => {
    if (questions[currentIndex]) {
      setTimeRemaining(questions[currentIndex].timer || 45);
    }
  }, [currentIndex, questions]);
  
  const handleOptionSelect = (questionId, optionIndex, isMultiple) => {
    const currentAnswer = answers[questionId] || (isMultiple ? [] : null);
    
    if (isMultiple) {
      const newAnswer = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
      const idx = newAnswer.indexOf(optionIndex);
      if (idx >= 0) {
        newAnswer.splice(idx, 1);
      } else {
        newAnswer.push(optionIndex);
      }
      setAnswers(prev => ({ ...prev, [questionId]: newAnswer }));
    } else {
      setAnswers(prev => ({ ...prev, [questionId]: [optionIndex] }));
    }
  };
  
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };
  
  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  const checkAnswer = (question, userAnswer) => {
    if (!userAnswer) return false;
    if (question.type === 'open') return userAnswer.length > 10;
    
    const correct = question.correct || [];
    if (question.type === 'unique') {
      return userAnswer[0] === correct[0];
    } else {
      const sortedUser = [...userAnswer].sort((a, b) => a - b);
      const sortedCorrect = [...correct].sort((a, b) => a - b);
      return JSON.stringify(sortedUser) === JSON.stringify(sortedCorrect);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    
    let totalPoints = 0;
    let earnedPoints = 0;
    const matiereScores = {};
    
    questions.forEach(q => {
      totalPoints += q.points;
      const userAnswer = answers[q.id];
      const isCorrect = checkAnswer(q, userAnswer);
      
      if (isCorrect) {
        earnedPoints += q.points;
      }
      
      const matiere = q.matiere || 'Général';
      if (!matiereScores[matiere]) {
        matiereScores[matiere] = { earned: 0, total: 0 };
      }
      matiereScores[matiere].total += q.points;
      if (isCorrect) {
        matiereScores[matiere].earned += q.points;
      }
    });
    
    const globalScore = Math.round((earnedPoints / totalPoints) * 100);
    const passed = globalScore >= 60;
    const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    
    setTimeout(() => {
      const result = {
        testId: test?.id,
        testTitle: test?.title,
        score: globalScore,
        passed,
        earnedPoints,
        totalPoints,
        answeredCount: Object.keys(answers).length,
        totalQuestions: questions.length,
        tabSwitches: tabSwitchCount,
        timeSpent,
        matiereScores,
        date: new Date()
      };
      onComplete?.(result);
      setSubmitting(false);
      onClose();
    }, 1500);
  };
  
  const isAnswered = () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return false;
    const answer = answers[currentQuestion.id];
    if (!answer) return false;
    if (Array.isArray(answer)) return answer.length > 0;
    if (typeof answer === 'string') return answer.trim().length > 0;
    return !!answer;
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };
  
  const getTimeStamp = () => {
    const elapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };
  
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;
  const isTimerWarning = timeRemaining <= 10;
  
  if (!open) return null;
  
  return (
    <>
      {/* Warning Overlay */}
      <Dialog
        open={showWarning}
        onClose={() => setShowWarning(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 48, color: '#DC3545', mb: 1 }} />
          <Typography variant="h6" fontWeight="bold" color="#DC3545">
            Tentative de Triche Détectée!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" textAlign="center">
            Vous avez tenté de changer d'onglet ou de fenêtre.
            <br />
            <strong>Nombre d'infractions: {tabSwitchCount}</strong>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => setShowWarning(false)}
            sx={{ bgcolor: '#0056A4' }}
          >
            J'ai compris, continuer le test
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Main Modal */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            height: '85vh',
            maxHeight: '85vh',
            overflow: 'hidden'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: '#1a1a2e',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor: '#FD7E14', width: 32, height: 32 }}>
                <EmojiEventsIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight="600">
                {test?.title} - Labellisation
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                icon={<AccessTimeIcon />}
                label={formatTime(timeRemaining || 0)}
                sx={{
                  bgcolor: isTimerWarning ? 'error.main' : 'warning.main',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#FFFFFF' }
                }}
              />
              <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              bgcolor: '#E9ECEF',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#28A745'
              }
            }}
          />
        </DialogTitle>
        
        {/* Content */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Question Area */}
          <DialogContent sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
            {currentQuestion && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: '#F8F9FA',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Chip
                  label={currentQuestion.matiere}
                  size="small"
                  sx={{
                    mb: 2,
                    bgcolor: 'rgba(0,86,164,0.1)',
                    color: '#0056A4',
                    fontWeight: 600
                  }}
                />
                
                <Typography variant="h6" fontWeight="600" sx={{ mb: 3, lineHeight: 1.5 }}>
                  {currentQuestion.question}
                </Typography>
                
                {currentQuestion.type === 'open' ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    placeholder="Votre réponse détaillée..."
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#FFFFFF',
                        '&:hover fieldset': {
                          borderColor: '#1976D2'
                        }
                      }
                    }}
                  />
                ) : currentQuestion.type === 'unique' ? (
                  <RadioGroup
                    value={answers[currentQuestion.id]?.[0] ?? ''}
                    onChange={(e) => handleOptionSelect(currentQuestion.id, parseInt(e.target.value), false)}
                  >
                    <Stack spacing={1.5}>
                      {currentQuestion.options.map((opt, idx) => (
                        <FormControlLabel
                          key={idx}
                          value={idx}
                          control={<Radio />}
                          label={opt}
                          sx={{
                            m: 0,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: answers[currentQuestion.id]?.[0] === idx ? 'primary.main' : 'divider',
                            bgcolor: answers[currentQuestion.id]?.[0] === idx ? 'action.hover' : 'background.paper',
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </RadioGroup>
                ) : (
                  <Stack spacing={1.5}>
                    {currentQuestion.options.map((opt, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            checked={answers[currentQuestion.id]?.includes(idx) || false}
                            onChange={() => handleOptionSelect(currentQuestion.id, idx, true)}
                          />
                        }
                        label={opt}
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: answers[currentQuestion.id]?.includes(idx) ? 'primary.main' : 'divider',
                          bgcolor: answers[currentQuestion.id]?.includes(idx) ? 'action.hover' : 'background.paper',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            )}
          </DialogContent>
          
          {/* Sidebar */}
          <Box
            sx={{
              width: 280,
              bgcolor: '#F8F9FA',
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              p: 2
            }}
          >
            {/* Progress Info */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                mb: 2,
                bgcolor: '#FFFFFF'
              }}
            >
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Progression
              </Typography>
              <Typography variant="h3" fontWeight="700" color="#0056A4">
                {answeredCount}/{questions.length}
              </Typography>
              <Typography variant="caption" color="#28A745">
                questions répondues
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">
                Question {currentIndex + 1} / {questions.length}
              </Typography>
            </Paper>
            
            {/* Log Screen */}
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                bgcolor: '#1e1e2e',
                borderRadius: 2,
                overflowY: 'auto',
                fontFamily: 'monospace'
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ color: '#cdd6f4' }}>
                  [{getTimeStamp()}] Test démarré
                </Typography>
                <Typography variant="caption" sx={{ color: '#cdd6f4' }}>
                  [{getTimeStamp()}] Question {currentIndex + 1} affichée
                </Typography>
                {tabSwitchCount > 0 && (
                  <Typography variant="caption" sx={{ color: '#f38ba8' }}>
                    [{getTimeStamp()}] ⚠️ Changement d'onglet détecté (x{tabSwitchCount})
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Box>
        </Box>
        
        {/* Footer */}
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            startIcon={<NavigateBeforeIcon />}
          >
            Précédent
          </Button>
          
          <Box sx={{ flex: 1 }} />
          
          {!isLastQuestion ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NavigateNextIcon />}
              sx={{ px: 3 }}
            >
              Suivant
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !isAnswered()}
              startIcon={!submitting && <SendIcon />}
              sx={{
                bgcolor: '#28A745',
                '&:hover': { bgcolor: '#218838' },
                px: 3
              }}
            >
              {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {submitting ? 'Soumission...' : 'Soumettre le test'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LabelingTest;